// Both the preview and the submitting form evaluate the selected variant.
export function getKlipSelectedProduct(product, variantId) {
  if (!product) return null
  const variant = variantId
    ? product.variants?.find((item) => String(item.id) === String(variantId))
    : product.variants?.[0]
  return variant ? { ...product, variants: [variant] } : null
}

function targetMatches(target, product) {
  if (!target || !product) return false
  const type = target.type || target.mode
  if (type === 'allProducts') return true
  const items = Array.isArray(target.selectedItems) ? target.selectedItems : []
  const variantIds = new Set((product.variants || []).map((variant) => String(variant.id)))
  if (type === 'productsAndVariants') {
    return items.some((item) => {
      const variantId = item.variantId || item.variant_id
      return variantId ? variantIds.has(String(variantId)) :
        String(item.id || item.product_id || item.productId) === String(product.id)
    })
  }
  if (type === 'products') {
    return items.map((item) => item.id || item.product_id || item.productId)
      .concat(target.productIds || []).some((id) => String(id) === String(product.id))
  }
  if (type === 'variants') {
    return items.map((item) => item.variantId || item.variant_id || item.id)
      .concat(target.variantIds || []).some((id) => variantIds.has(String(id)))
  }
  if (type === 'collections') {
    const ids = new Set((product.collectionIds || []).map(String))
    return items.map((item) => item.id || item.collection_id || item.collectionId)
      .concat(target.collectionIds || []).some((id) => ids.has(String(id)))
  }
  if (type === 'tags') {
    const tags = new Set((product.tags || []).map((tag) => String(tag).trim().toLowerCase()))
    const selected = Array.isArray(target.tags) ? target.tags : String(target.tags || '').split(',')
    return selected.some((tag) => tags.has(String(tag).trim().toLowerCase()))
  }
  if (type === 'selectedItems') {
    return items.some((item) => String(item.id || item.product_id || item.productId || '') === String(product.id) ||
      variantIds.has(String(item.variant_id || item.variantId || '')))
  }
  return false
}

export function klipCouponAppliesToProduct(coupon, product, { claimed = false } = {}) {
  if (!coupon?.published || coupon.type !== 'line_item' || !product) return false
  if (coupon.visibility?.productPage === false) return false
  const now = Date.now()
  const startsAt = Date.parse(coupon.startDate)
  const endsAt = Date.parse(coupon.endDate)
  if ((Number.isFinite(startsAt) && startsAt > now) || (Number.isFinite(endsAt) && endsAt < now)) return false
  if (!targetMatches(coupon.target, product)) return false
  const excluded = coupon.target?.exclude
  if (excluded?.enabled && targetMatches(excluded, product)) return false
  if (coupon.target?.excludeCompareAtPrice && product.variants.some((variant) => Number(variant.compare_at_price) > Number(variant.price))) return false
  // An active official widget has already checked claim/login/marketing rules.
  // Only unsolicited previews and fallback coupons require automatic claiming.
  if (claimed) return true
  if (!coupon.autoClipped || (coupon.discountMethod && coupon.discountMethod !== 'automatic')) return false
  if (coupon.customerLoginRequired && !window.KLIP_APP_DATA?.loggedIn) return false
  if (coupon.onePerCustomer || coupon.leadCapture?.enabled || coupon.spend?.enabled || coupon.discountConditions?.OR?.length) return false
  if (coupon.marketingTarget?.utm?.utmTargeting || coupon.marketingTarget?.location?.enabled || coupon.marketingTarget?.customer?.enabled) return false
  if (coupon.usageLimit && Number(coupon.usage || 0) >= Number(coupon.usageLimit)) return false
  return true
}

export function selectKlipAutoCoupon(coupons, product) {
  const matches = (coupons || []).filter((coupon) => klipCouponAppliesToProduct(coupon, product))
  if (window.KLIP_APP_DATA?.couponsPriority === 'highest_discount') {
    const score = (coupon) => coupon.discount?.type === 'percentage'
      ? Number(product.variants[0].price) * Number(coupon.discount.value) / 100
      : Number(coupon.discount?.value) * 100
    matches.sort((a, b) => score(b) - score(a))
  }
  return matches[0] || null
}

// Recommended forms must not submit before the coupon app and the cart listener
// can bind the offer. Unrelated images, videos and window.load are not involved.
export function waitForKlipCartReady({ signal, timeout = 15000 } = {}) {
  const requiresKlip = Boolean(window.KLIP_APP_DATA ||
    document.querySelector('.elsklip-coupon-box[data-klip-product]') ||
    Array.from(document.scripts).some((script) => script.src.includes('/klip-app.js')))
  const startedAt = Date.now()
  return new Promise((resolve, reject) => {
    let timer
    const finish = (error) => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
      if (error) reject(error)
      else resolve()
    }
    const onAbort = () => finish(new DOMException('The product form was removed.', 'AbortError'))
    const check = () => {
      if (signal?.aborted) return onAbort()
      if (requiresKlip) warmKlipCoupons()
      const coupons = window.KLIP_APP_DATA?.coupons
      const couponReady = !requiresKlip || (Array.isArray(coupons) &&
        (!coupons.length || Boolean(window.elsklip?.app?.productCoupons)))
      if (window.CartSlideDrawer?.ready && couponReady) return finish()
      if (Date.now() - startedAt >= timeout) {
        const error = new Error('Recommended cart dependencies did not become ready')
        error.description = 'Your cart is still loading. Please try again.'
        return finish(error)
      }
      timer = setTimeout(check, 50)
    }
    signal?.addEventListener('abort', onAbort, { once: true })
    check()
  })
}

let klipWarmupStarted=!1
export function warmKlipCoupons(){
if(window.elsklip&&window.elsklip.app)return
const scripts=Array.from(document.scripts)
const activeScript=scripts.find(script=>script.src.includes('/klip-app.js')&&!/^[a-f0-9]+-text\/javascript$/i.test(script.type))
if(activeScript)return
const deferredScript=scripts.find(script=>script.src.includes('/klip-app.js'))
const styleAsset=document.querySelector('link[href*="klip-product-page-coupons"][href*="/assets/klip-app.css"]')
const scriptUrl=deferredScript&&deferredScript.src||(styleAsset&&styleAsset.href.replace(/\/klip-app\.css(?:\?.*)?$/,'/klip-app.js'))
if(!scriptUrl||klipWarmupStarted)return
klipWarmupStarted=!0
const configScript=scripts.find(script=>!script.src&&/^[a-f0-9]+-text\/javascript$/i.test(script.type)&&/\bKLIP_APP_DATA\s*=/.test(script.textContent))
if(configScript&&!window.KLIP_APP_DATA){const runner=document.createElement('script');runner.setAttribute('data-cfasync','false');runner.textContent=configScript.textContent;document.head.appendChild(runner);runner.remove();configScript.remove()}
const preconnect=document.createElement('link');preconnect.rel='preconnect';preconnect.href='https://app.klipcoupons.com';preconnect.crossOrigin='anonymous';preconnect.setAttribute('data-klip-quick-view-resource','');document.head.appendChild(preconnect)
const preload=document.createElement('link');preload.rel='preload';preload.as='script';preload.href=scriptUrl;preload.fetchPriority='high';preload.setAttribute('data-klip-quick-view-resource','');document.head.appendChild(preload)
const runner=document.createElement('script');runner.src=scriptUrl;runner.async=!0;runner.fetchPriority='high';runner.setAttribute('data-cfasync','false');runner.setAttribute('data-klip-quick-view-loader','')
runner.addEventListener('error',()=>{klipWarmupStarted=!1;runner.remove();preload.remove()},{once:!0});document.head.appendChild(runner)
if(deferredScript)deferredScript.remove()}
